import { Component, Inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MegapayLoaderComponent } from 'src/app/shared/component/megapay-loader/megapay-loader.component';
import { DropdownFieldV2Component } from 'src/app/shared/component/custom-dropdown-v2/custom-dropdown-v2.component';
import * as vo from 'src/app/core/model/server/vo/_index';
import { RebateService } from '../rebate.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SharedDirectivesModule } from 'src/app/shared/modules/shared-directives.module';

@Component({
  selector: 'app-create-rebate',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    NgxSpinnerModule,
    MegapayLoaderComponent,
    DropdownFieldV2Component,
    MatTooltipModule,
    SharedDirectivesModule
  ],
  templateUrl: './create-rebate.component.html',
  styleUrl: './create-rebate.component.scss',
})
export class CreateRebateComponent implements OnDestroy {
  private destroy$ = new Subject<void>();

  isLoading = false;

  providerOptions: { label: string; value: number }[];
  tierOptions: { label: string; value: number }[];

  selectedTierId: number | null = null;
  selectedTierText = '';
  selectedProviderType: number | null = null;
  selectedProviderText = '';
  rebatePercentage: number | null = null;

  constructor(
    public dialogRef: MatDialogRef<CreateRebateComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { tierId: number; tierLabel: string },
    private rebateService: RebateService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
  ) {
    this.providerOptions = this.rebateService.providerOptions;
    this.tierOptions     = this.rebateService.tierOptions;

    this.selectedTierId  = this.data.tierId;
    this.selectedTierText = this.data.tierLabel;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onTierChange(option: { label: string; value: any }): void {
    this.selectedTierId  = option.value;
    this.selectedTierText = option.label;
  }

  onProviderChange(option: { label: string; value: any }): void {
    this.selectedProviderType = option.value;
    this.selectedProviderText = option.label;
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  submit(): void {
    if (this.selectedTierId === null) {
      this.toastr.error('Please select a Tier.');
      return;
    }
    if (this.selectedProviderType === null) {
      this.toastr.error('Please select a Network Provider.');
      return;
    }
    if (this.rebatePercentage === null || this.isPercentageInvalid) {
      this.toastr.error('Please enter a valid Rebate Percentage (0-100).');
      return;
    }

    const request: vo.CreateBuyloadRebateRequest = {
      tierId:           this.selectedTierId,
      providerType:     this.selectedProviderType,
      rebatePercentage: this.rebatePercentage,
    };

    this.isLoading = true;
    this.spinner.show().catch(console.error);

    this.rebateService.createRebate(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response.status === 200) {
            this.toastr.success('Rebate Created Successfully.');
            this.dialogRef.close(true);
          } else {
            this.toastr.error(response.error?.message || 'Unknown error', 'Failed to create rebate');
          }
        },
        error: (err) => {
          const message =
            err?.error?.message || 
            err?.message || 
            'An error occurred while creating the rebate.';

          this.toastr.error(message, 'Failed to create rebate');
        },
        complete: () => {
          this.isLoading = false;
          this.spinner.hide().catch(console.error);
        },
      });
  }

  get isPercentageInvalid(): boolean {
    return this.rebatePercentage !== null && (this.rebatePercentage < 0 || this.rebatePercentage > 100);
  }

}
